import axios, { type AxiosError } from 'axios'
import { clearSession, getToken } from '@/shared/auth/tokenStorage'

/**
 * API manzili uch bosqichda aniqlanadi (xsib-admin naqshi):
 *   1. `public/config.js` dagi `window.__XSIB_SELFCARE_API_BASE__` —
 *      serverda tahrirlanadi, build qayta yig'ilmaydi
 *   2. `VITE_API_BASE_URL` — build vaqtidagi qiymat
 *   3. bo'sh — so'rovlar "/api/..." ga ketadi (dev'da Vite proxy,
 *      prod'da nginx backend'ga uzatadi)
 */
declare global {
  interface Window {
    __XSIB_SELFCARE_API_BASE__?: string
  }
}

const runtimeBase = typeof window !== 'undefined' ? window.__XSIB_SELFCARE_API_BASE__ : undefined

export const api = axios.create({
  baseURL: (runtimeBase ?? import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, ''),
  headers: { 'Content-Type': 'application/json' },
})

// Har so'rovga token qo'shiladi
api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Backend javobining yagona shakli (`uz.xsib.common.response.XsibResponse`).
 * Sahifalar buni ko'rmaydi — quyidagi interceptor ochib tashlaydi.
 */
export interface XsibEnvelope<T> {
  success: boolean
  requestId: string | null
  timeZone: string | null
  error: { code: number | null; tag: string | null; text: string | null } | null
  data: T
}

function isEnvelope(body: unknown): body is XsibEnvelope<unknown> {
  return typeof body === 'object' && body !== null && 'success' in body && 'data' in body
}

/** Envelope ichidagi xato — `translateError` uni `message` orqali o'qiydi. */
export class XsibApiError extends Error {
  constructor(
    message: string,
    readonly tag: string | null,
    readonly code: number | null,
    readonly requestId: string | null,
  ) {
    super(message)
    this.name = 'XsibApiError'
  }
}

/**
 * Envelope BITTA joyda ochiladi (xsib-admin bilan bir xil naqsh).
 *
 * ⚠️ Har chaqiruvda `res.data.data` yozish o'nlab joyda takrorlanardi va
 * bittasini unutish yetarli edi — unutilgani faqat ekranda `undefined`
 * chiqqanda bilinardi. Zod sxemalari TEGILMAYDI: ular ichki (`data`)
 * shaklni tekshiradi va interceptor ularga aynan o'shani beradi.
 */
api.interceptors.response.use((response) => {
  const body: unknown = response.data
  if (isEnvelope(body)) {
    if (!body.success) {
      return Promise.reject(
        new XsibApiError(
          // ⚠️ Qotirilgan o'zbekcha matn TURMASIN — u `translateError` orqali
          // to'g'ridan-to'g'ri ekranga chiqadi. Bo'sh satr `errors.generic` ga tushadi.
          body.error?.text ?? '',
          body.error?.tag ?? null,
          body.error?.code ?? null,
          body.requestId,
        ),
      )
    }
    response.data = body.data
  }
  return response
})

/**
 * 401 → tokenni tozalab loginga qaytar.
 *
 * ⚠️ Bu yerda xsib-admin'dagi kabi "faqat token haqiqatan yaroqsiz bo'lsa"
 * degan qo'shimcha shart YO'Q — selfcare zanjiri (`SelfcareSecurityConfig`)
 * soddaroq: himoyalangan har bir yo'l `anyRequest().authenticated()` bilan
 * yopilgan va autentifikatsiyadan o'tmagan so'rov to'g'ridan-to'g'ri
 * `HttpStatusEntryPoint(401)` dan qaytadi — admin panelidagi `/error`
 * orqali kelib qoladigan noaniq 401 muammosi bu yerda yo'q (Spring bu
 * kontrollerlarga aynan shu yo'l bilan yetib bormaydi). `/api/selfcare/login`
 * dagi noto'g'ri parol ham 401 qaytaradi, lekin bu holatda foydalanuvchi
 * allaqachon `/login` sahifasida — tozalash zararsiz, qayta yo'naltirish
 * bo'lmaydi.
 */
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const onLoginPage = window.location.pathname === '/login'
      clearSession()
      if (!onLoginPage) {
        window.location.replace('/login')
      }
    }
    return Promise.reject(error)
  },
)
